import styles from './LineChart.module.css';
import React, { useRef, useState, useEffect } from 'react';
import * as d3 from 'd3';

export default function LineChart({ data }) {
  const prevDataRef = useRef(null);
  const svgRef = useRef();
  const gRef = useRef();
  const yScaleRef = useRef();
  const wrapperRef = useRef();
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    value: '',
    threshold: '',
    backgroundColor: '#242424',
    name: ''
  });
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const preDimensionsRef = useRef({ width: 0, height: 0 });
  const [hoverY, setHoverY] = useState(null);

  useEffect(() => {
    const observer = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      setDimensions({ width, height });
    });
    if (wrapperRef.current) observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const sameData =
      JSON.stringify(prevDataRef.current) === JSON.stringify(data);
    const sameSize =
      preDimensionsRef.current.width === dimensions.width &&
      preDimensionsRef.current.height === dimensions.height;

    if (sameData && sameSize) return;

    prevDataRef.current = data;
    preDimensionsRef.current = dimensions;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const margin = { top: 40, right: 40, bottom: 50, left: 80 };
    const innerWidth = Math.max(0, dimensions.width - margin.left - margin.right);
    const innerHeight = Math.max(0, dimensions.height - margin.top - margin.bottom);

    const g = svg
      .attr('width', dimensions.width)
      .attr('height', dimensions.height)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    gRef.current = g;

    const cleanedData = (data ?? []).map(line => ({
      ...line,
      data: (line?.data ?? []).filter(
        p =>
          p &&
          p.x !== undefined &&
          p.y !== undefined &&
          Number.isFinite(p.y)
      )
    }));

    const allModels = Array.from(
      new Set(cleanedData.flatMap(d => d.data.map(p => p.x)))
    );
    const x0 = d3
      .scaleBand()
      .domain(allModels)
      .range([0, innerWidth])
      .padding(0.2);

    const defs = svg.append('defs');
    defs
      .append('filter')
      .attr('id', 'glow')
      .append('feGaussianBlur')
      .attr('stdDeviation', 4)
      .attr('result', 'coloredBlur');

    const xScale = d3
      .scalePoint()
      .domain(allModels)
      .range([0, innerWidth])
      .padding(0.5);

    const allYValues = cleanedData.flatMap(d => d.data.map(p => p.y));
    const hasY = allYValues.length > 0;
    const yMin = hasY ? Math.min(...allYValues) : 0;
    const yDomainMin = hasY ? Math.max(0, yMin - 0.05) : 0;
    const yStart = hasY ? Math.floor(yMin * 10) / 10 : 0.0;
    const yEnd = 1.0;

    const yScale = d3.scaleLinear().domain([yDomainMin, yEnd]).range([innerHeight, 0]);
    yScaleRef.current = yScale;

    for (let yVal = yStart; yVal <= yEnd + 0.0001; yVal += 0.1) {
      const y = yScale(yVal);
      g.append('line')
        .attr('x1', 0)
        .attr('x2', innerWidth)
        .attr('y1', y)
        .attr('y2', y)
        .attr('stroke', '#363636')
        .attr('stroke-width', 1.5)
        .attr('stroke-linecap', 'round')
        .attr('stroke-dasharray', '8 20');

      g.append('text')
        .text(yVal.toFixed(1))
        .attr('x', -40)
        .attr('y', y)
        .attr('fill', 'var(--color-placeholder)')
        .style('font-size', '12px')
        .style('font-weight', 'bold')
        .attr('alignment-baseline', 'middle');
    }

    const xAxis = g
      .append('g')
      .attr('transform', `translate(0, ${innerHeight})`)
      .call(d3.axisBottom(x0));

    xAxis
      .selectAll('text')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .each(function (d) {
        const text = d3.select(this);
        const maxChars = 8;
        const label = d?.toString?.() ?? '';
        const shortLabel = label.length > maxChars ? label.slice(0, maxChars) + '...' : label;
        text.text(shortLabel);
        text.append('title').text(label);
      })
      .select('path')
      .attr('stroke-width', 2)
      .selectAll('line')
      .remove();

    const line = d3
      .line()
      .x(d => xScale(d.x))
      .y(d => yScale(d.y))
      .curve(d3.curveMonotoneX);

    cleanedData.forEach((testLine, i) => {
      if (!testLine.data || testLine.data.length === 0) return;

      g.append('path')
        .datum(testLine.data)
        .attr('class', 'line')
        .attr('id', `line-${i}-glow`)
        .attr('fill', 'none')
        .attr('stroke', testLine.color)
        .attr('stroke-width', 2)
        .attr('filter', 'url(#glow)')
        .attr('d', line)
        .attr('stroke-dasharray', function () {
          const length = this.getTotalLength();
          return `${length} ${length}`;
        })
        .attr('stroke-dashoffset', function () {
          return this.getTotalLength();
        })
        .transition()
        .duration(1000)
        .ease(d3.easeCubicInOut)
        .attr('stroke-dashoffset', 0);

      g.append('path')
        .datum(testLine.data)
        .attr('class', 'line')
        .attr('id', `line-${i}`)
        .attr('fill', 'none')
        .attr('stroke', testLine.color)
        .attr('stroke-width', 2)
        .attr('d', line)
        .attr('stroke-dasharray', function () {
          const length = this.getTotalLength();
          return `${length} ${length}`;
        })
        .attr('stroke-dashoffset', function () {
          return this.getTotalLength();
        })
        .transition()
        .duration(1000)
        .ease(d3.easeCubicInOut)
        .attr('stroke-dashoffset', 0);

      g.append('path')
        .datum(testLine.data)
        .attr('fill', 'none')
        .attr('stroke', 'transparent')
        .attr('stroke-width', 20)
        .on('mouseover', () => {
          g.selectAll('.line')
            .filter(function () {
              return ![`line-${i}`, `line-${i}-glow`, `line-${i}-hover`].includes(this?.id);
            })
            .transition()
            .duration(250)
            .attr('opacity', 0.1);
          g.selectAll('circle')
            .filter(function () {
              return !this?.classList?.contains?.(`dot-${i}`);
            })
            .transition()
            .duration(250)
            .attr('opacity', 0.1);
        })
        .on('mouseout', () => {
          cleanedData.forEach(item => {
            g.selectAll('.line').attr('stroke', item.color).attr('opacity', 1);
            g.selectAll('circle').attr('stroke', item.color).attr('opacity', 1);
          });
        })
        .attr('d', line);

      g.selectAll(`.dot-${i}-glow`)
        .data(testLine.data)
        .enter()
        .append('circle')
        .attr('class', `dot-${i}-glow`)
        .attr('cx', d => xScale(d.x))
        .attr('cy', d => yScale(d.y))
        .attr('r', 0)
        .attr('fill', testLine.color)
        .attr('filter', 'url(#glow)')
        .transition()
        .delay((_, j) => (j * 1000) / testLine.data.length)
        .duration(600)
        .ease(d3.easeCubicOut)
        .attr('r', 6);

      g.selectAll(`.dot-${i}`)
        .data(testLine.data)
        .enter()
        .append('circle')
        .attr('class', `dot-${i}`)
        .attr('cx', d => xScale(d.x))
        .attr('cy', d => yScale(d.y))
        .attr('r', 0)
        .attr('fill', '#242424')
        .attr('stroke', testLine.color)
        .attr('stroke-width', 2)
        .on('mousemove', (event, d) => {
          const [x, y] = d3.pointer(event);
          setTooltip({
            visible: true,
            x: x + margin.left + 20,
            y: y + margin.top - 20,
            value: (d.y ?? 0).toFixed(3),
            threshold: d.z != null && Number.isFinite(d.z) ? d.z.toFixed(3) : '-',
            backgroundColor: testLine.color,
            name: testLine.datasetName
          });
        })
        .on('mouseout', () => setTooltip({ visible: false }))
        .transition()
        .delay((_, j) => (j * 1000) / testLine.data.length)
        .duration(600)
        .ease(d3.easeCubicOut)
        .attr('r', 6);
    });

    svg.on('mousemove', function (e) {
      const [, mouseY] = d3.pointer(e);
      const chartY = mouseY - margin.top;
      if (chartY < 0 || chartY > innerHeight) {
        setHoverY(null);
        return;
      }
      const yValue = yScale.invert(chartY);
      setHoverY(yValue);
    });

    svg.on('mouseleave', () => {
      setHoverY(null);
    });
  }, [data, dimensions]);

  useEffect(() => {
    const g = gRef.current;
    const yScale = yScaleRef.current;

    if (!g || !yScale) return;

    g.select('#hover-line')?.remove();
    g.select('#hover-label')?.remove();
    g.select('#hover-label-bg')?.remove();

    if (hoverY === null) return;

    const y = yScale(hoverY);
    const innerWidth = Math.max(0, (preDimensionsRef.current.width || 0) - 80);

    g.append('line')
      .attr('id', 'hover-line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', y)
      .attr('y2', y)
      .attr('stroke', 'var(--color-placeholder')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '4 4')
      .attr('pointer-events', 'none')
      .lower();

    g.append('rect')
      .attr('id', 'hover-label-bg')
      .attr('x', -60)
      .attr('y', y - 14)
      .attr('width', 50)
      .attr('height', 26)
      .attr('rx', 6)
      .attr('ry', 6)
      .style('fill', 'var(--color-white)')
      .style('opacity', 0.8)
      .attr('pointer-events', 'none');

    g.append('text')
      .attr('id', 'hover-label')
      .text(hoverY.toFixed(3))
      .attr('x', -50)
      .attr('y', y)
      .attr('alignment-baseline', 'middle')
      .style('font-size', 'var(--font-size-xs)')
      .style('fill', 'var(--color-dark)')
      .style('font-weight', 'bold')
      .attr('pointer-events', 'none');
  }, [hoverY]);

  return (
    <div ref={wrapperRef} style={{ width: '100%', height: '100%', position: 'relative' }}>
      <svg ref={svgRef} />
      {tooltip.visible && (
        <div
          className={styles['tooltip']}
          style={{
            left: tooltip.x,
            top: tooltip.y
          }}
        >
          <div>
            <div
              style={{
                backgroundColor: tooltip.backgroundColor
              }}
            />
            <span>{tooltip.name}</span>
          </div>
          <span>Accuracy: {tooltip.value}</span>
          <span>Threshold: {tooltip.threshold}</span>
        </div>
      )}
    </div>
  );
};